import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useRedirect from "@/hooks/useRedirect";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { CASH_INDEX_PAGE, REGISTRATION_PAGE, SHOP_CHECKOUT_PAGE, TERMS_OF_REFUND_PAGE, TERMS_OF_USE_PAGE } from "@/settings/constant";
import { Button, RadioGroup, Spinner, Typography } from "@bookips/sds";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useDialogStore } from "src/store/useDialogStore";
import useSnackbar from "src/store/useSnackbar";
import CashProductItem from "./CashProductItem";
import useGetCashBalance from "./hooks/useGetCashBalance";
import useGetCashProducts from "./hooks/useGetCashProducts";
import useVerification from "./hooks/useVerification";
import VerificationDialog from "./VerificationDialog";
export const NICE_FORM_URL = "https://nice.checkplus.co.kr/CheckPlusSafeModel/checkplus.cb";
export const REDIRECT_FROM_AUTH_KEY = "redirectFromAuthToCash";
export const MAX_CASH_BALANCE = 2000000;
export const getOrigin = ()=>{
    if ("object" !== "undefined") {
        return window.location.origin;
    }
    return "";
};
const CashCharge = ()=>{
    var _error_response;
    const router = useRouter();
    const { isLoggedIn , user  } = useAuthStore();
    const { open , close  } = useDialogStore();
    const { redirectTo , redirectToLogin  } = useRedirect();
    const { addSnackbar  } = useSnackbar();
    const { data: cashBalance  } = useGetCashBalance();
    const { data: cashProducts , isLoading , isError , error  } = useGetCashProducts();
    const is500Error = isError && (error === null || error === void 0 ? void 0 : (_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === 500;
    const { ref , inView  } = useInView();
    const [isVerificationDialogOpen, setIsVerificationDialogOpen] = useState(false);
    const [selectedCash, setSelectedCash] = useState(()=>{
        if ("object" !== "undefined") {
            const savedCashId = localStorage.getItem("selectedCash");
            if (savedCashId) return savedCashId;
        }
        if (cashProducts === null || cashProducts === void 0 ? void 0 : cashProducts.products) {
            var _cashProducts_products_;
            const defaultProduct = cashProducts.products.find((product)=>product.isDefault);
            if (defaultProduct) {
                return String(defaultProduct.id);
            }
            if ((_cashProducts_products_ = cashProducts.products[0]) === null || _cashProducts_products_ === void 0 ? void 0 : _cashProducts_products_.id) {
                return String(cashProducts.products[0].id);
            }
        }
        return "";
    });
    useVerification({
        selectedCash,
        setIsVerificationDialogOpen
    });
    useEffect(()=>{
        if (is500Error) {
            addSnackbar({
                message: "다시 시도해주세요.",
                variant: "error"
            });
        }
    }, [
        is500Error
    ]);
    // 캐시 상품 선택
    useEffect(()=>{
        if (cashProducts && cashProducts.products.length > 0) {
            const saved = localStorage.getItem("selectedCash");
            if (saved && cashProducts.products.some((product)=>String(product.id) === saved)) {
                setSelectedCash(saved);
            } else {
                const defaultProduct = cashProducts.products.find((product)=>product.isDefault);
                if (defaultProduct) {
                    setSelectedCash(String(defaultProduct.id));
                } else {
                    setSelectedCash(String(cashProducts.products[0].id));
                }
            }
        }
    }, [
        cashProducts
    ]);
    const handleCharge = ()=>{
        var _cashProducts_products_find;
        mixpanelEvent("CTA Button Clicked", {
            "Button Type": "Cash",
            "Button URL": "".concat(SHOP_CHECKOUT_PAGE, "?id=").concat(selectedCash, "&orderType=cash")
        });
        // 1. 비로그인 시
        if (!isLoggedIn) {
            mixpanelEvent("Popup Viewed", {
                Title: "로그인이 필요해요.",
                position: "캐시 충전"
            });
            open({
                id: "login-required",
                type: "confirm",
                props: {
                    title: "로그인이 필요해요.",
                    description: "로그인 또는 회원가입을 진행해주세요.",
                    confirmText: "회원가입",
                    cancelText: "로그인",
                    onConfirm: ()=>{
                        mixpanelEvent("Popup Button Clicked", {
                            "Popup Title": "로그인이 필요해요.",
                            "Button Name": "회원가입",
                            position: "캐시 충전"
                        });
                        localStorage.setItem("selectedCash", selectedCash);
                        localStorage.setItem(REDIRECT_FROM_AUTH_KEY, "true");
                        redirectTo(REGISTRATION_PAGE, CASH_INDEX_PAGE);
                    },
                    onCancel: ()=>{
                        mixpanelEvent("Popup Button Clicked", {
                            "Popup Title": "로그인이 필요해요.",
                            "Button Name": "로그인",
                            position: "캐시 충전"
                        });
                        localStorage.setItem("selectedCash", selectedCash);
                        localStorage.setItem(REDIRECT_FROM_AUTH_KEY, "true");
                        redirectToLogin(CASH_INDEX_PAGE);
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            close("login-required");
                        }
                    }
                }
            });
            return;
        }
        // 2. 나이 정보가 없는 경우
        if (!user.birth) {
            localStorage.setItem("selectedCash", selectedCash);
            mixpanelEvent("identification Popup Viewed", {
                "Popup Title": "안전한 이용을 위해 본인 인증이 필요해요.",
                position: "cash",
                Category: "identification required"
            });
            setIsVerificationDialogOpen(true);
            return;
        }
        var _cashBalance_totalBalance;
        // 3. 캐시 한도 초과 시
        const balance = (_cashBalance_totalBalance = cashBalance === null || cashBalance === void 0 ? void 0 : cashBalance.totalBalance) !== null && _cashBalance_totalBalance !== void 0 ? _cashBalance_totalBalance : 0;
        var _cashProducts_products_find_price;
        const selectedCashAmount = (_cashProducts_products_find_price = (_cashProducts_products_find = cashProducts === null || cashProducts === void 0 ? void 0 : cashProducts.products.find((product)=>product.id === Number(selectedCash))) === null || _cashProducts_products_find === void 0 ? void 0 : _cashProducts_products_find.price) !== null && _cashProducts_products_find_price !== void 0 ? _cashProducts_products_find_price : 0;
        mixpanelEvent("Popup Viewed", {
            Title: "캐시 한도를 초과했어요.",
            position: "캐시 한도"
        });
        if (balance + selectedCashAmount > MAX_CASH_BALANCE) {
            open({
                id: "cash-balance-exceeded",
                type: "confirm",
                props: {
                    title: "캐시 한도를 초과했어요.",
                    description: "보유 캐시와 충전 예정 금액의 합이 한도 200만 원을 초과해요.\n일부 캐시를 사용 후 다시 시도해 주세요.",
                    onConfirm: ()=>{
                        close("cash-balance-exceeded");
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            close("cash-balance-exceeded");
                        }
                    }
                }
            });
            return;
        }
        router.push("".concat(SHOP_CHECKOUT_PAGE, "?id=").concat(selectedCash, "&orderType=cash"));
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(VerificationDialog, {
                open: isVerificationDialogOpen,
                title: "결제 전 본인 인증이 필요해요.",
                description: "나이 확인을 위해 본인 인증이 필요해요.\n완료되면 결제를 진행할 수 있어요.",
                onClose: ()=>setIsVerificationDialogOpen(false)
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "rounded-md rounded-t-none border border-t-0 border-gray-100 bg-white pb-1",
                children: isLoading || is500Error ? /*#__PURE__*/ _jsx("div", {
                    className: "flex h-[200px] items-center justify-center",
                    children: /*#__PURE__*/ _jsx(Spinner, {
                        size: "lg",
                        className: "text-primary-main"
                    })
                }) : /*#__PURE__*/ _jsx(_Fragment, {
                    children: (cashProducts === null || cashProducts === void 0 ? void 0 : cashProducts.products.length) > 0 ? /*#__PURE__*/ _jsx(RadioGroup, {
                        value: selectedCash,
                        onValueChange: (value)=>setSelectedCash(value),
                        className: "flex flex-col",
                        children: cashProducts === null || cashProducts === void 0 ? void 0 : cashProducts.products.map((product)=>/*#__PURE__*/ _jsx(CashProductItem, {
                                product: product,
                                checked: selectedCash === String(product.id)
                            }, product.id))
                    }) : /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        className: "text-muted flex h-[200px] items-center justify-center",
                        children: "캐시 상품을 준비 중이에요."
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle2",
                                className: "text-secondary",
                                children: "유의사항"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-secondary mt-2",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body2",
                                        className: "text-secondary mb-1",
                                        children: "충전"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "캐시는 쏠북 내에서 다양한 상품을 편리하게 결제할 수 있는 결제수단입니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "충전한 캐시는 자료, 제본, 실물배송 상품, 이용권(쏠북패스\xb7엑스퍼트 등) 결제에 사용할 수 있습니다. 단, 굿노트 e-book(전자책) 구매에는 사용이 불가합니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "캐시는 최대 200만 원까지 충전할 수 있으며, 신용카드 충전 시 카드사 정책에 따라 충전 한도가 달라질 수 있습니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "충전 금액에 따라 무료 포인트가 혜택으로 적립될 수 있으며, 적립된 포인트는 포인트 사용 정책에 따라 쏠북 내에서 사용할 수 있습니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "카드로 충전한 캐시는 충전 시점에 부가세 신고가 이루어지며, 카드 매출전표로 결제 증빙이 가능합니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "계좌이체 등 현금으로 충전한 캐시는 충전 시점이 아닌 캐시 사용 시점의 사용 금액 기준으로 현금영수증이 발급됩니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "캐시 충전은 문화비소득공제 신청을 할 수 없습니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body2",
                                        className: "text-secondary mb-1",
                                        children: "사용\xb7소멸"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "캐시는 먼저 충전한 순서대로 사용됩니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "상품 결제 페이지에서 결제수단으로 캐시 또는 포인트를 선택하여 사용할 수 있습니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "캐시는 마지막 사용일로부터 5년이 경과하면 「상법 제64조 상사채권 소멸시효」에 따라 소멸됩니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "포인트는 명시된 만료일 이전까지 사용 가능하며, 만료일 경과 시 자동 소멸됩니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsxs(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: [
                                                    "캐시/포인트와 관련된 상세 안내 내용은",
                                                    " ",
                                                    /*#__PURE__*/ _jsx(Link, {
                                                        href: TERMS_OF_USE_PAGE,
                                                        target: "_blank",
                                                        onClick: ()=>{
                                                            mixpanelEvent("Guide Button Clicked", {
                                                                "Guide Categories": "쏠북 이용약관",
                                                                "Guide URL": TERMS_OF_USE_PAGE
                                                            });
                                                        },
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            as: "span",
                                                            variant: "body2",
                                                            className: "text-secondary-main cursor-pointer underline",
                                                            children: "쏠북 이용약관"
                                                        })
                                                    }),
                                                    "을 확인해 주세요."
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body2",
                                        className: "text-secondary mb-1",
                                        children: "환불"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: "충전한 캐시는 결제 후 7일 이내에 결제취소가 가능합니다. 단, 캐시 또는 캐시 충전 혜택으로 적립된 포인트를 일부라도 사용한 경우에는 결제취소가 불가합니다."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-secondary mr-2 flex-shrink-0",
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsxs(Typography, {
                                                variant: "body2",
                                                className: "text-secondary flex-1",
                                                children: [
                                                    "일정 금액 이상을 사용한 후 남은 잔액은 환급이 가능하며, 구체적인 기준과 절차는",
                                                    " ",
                                                    /*#__PURE__*/ _jsx(Link, {
                                                        href: TERMS_OF_REFUND_PAGE,
                                                        target: "_blank",
                                                        onClick: ()=>{
                                                            mixpanelEvent("Guide Button Clicked", {
                                                                "Guide Categories": "취소\xb7환불 정책",
                                                                "Guide URL": TERMS_OF_REFUND_PAGE
                                                            });
                                                        },
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            as: "span",
                                                            variant: "body2",
                                                            className: "text-secondary-main cursor-pointer underline",
                                                            children: "취소\xb7환불 정책"
                                                        })
                                                    }),
                                                    "에 따릅니다."
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    (cashProducts === null || cashProducts === void 0 ? void 0 : cashProducts.products.length) > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                size: "xl",
                                ref: ref,
                                onClick: handleCharge,
                                children: "캐시 충전하기"
                            }),
                            !inView && /*#__PURE__*/ _jsx("div", {
                                className: "fixed bottom-5 left-1/2 z-50 w-full max-w-[640px] -translate-x-1/2 transform px-5",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    size: "xl",
                                    className: "w-full",
                                    onClick: handleCharge,
                                    children: "캐시 충전하기"
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default CashCharge;
